import { Tooltip } from 'antd';
import type { ReactNode } from 'react';
import { Button } from './Button';
import type { ButtonProps } from './types';

export interface SelectionButtonProps extends Omit<ButtonProps, 'children' | 'disabled'> {
    /** Number of rows currently selected in the list the button acts on. */
    selectedCount: number;
    /** The action, sentence case, e.g. "Reassign". */
    action: string;
    /** Plural noun for the rows, e.g. "claims". */
    noun: string;
    /** Singular noun for one selected row, e.g. "claim" or "policy". */
    nounSingular: string;
    /** Override the selection hint when the action needs a different sentence structure. */
    selectionHint?: string;
    /**
     * What unblocks the action when other state blocks it (another batch is running, a setting is
     * off). Setting it disables the button whatever the selection, so a block always explains
     * itself; it is shown on hover or focus in place of the selection hint.
     */
    disabledReason?: ReactNode;
}

const isBlocked = (reason: ReactNode) => reason !== undefined && reason !== null && reason !== false && reason !== '';

/**
 * Bulk action for a list with row selection.
 *
 * The button stays visible so the action is discoverable, and is disabled until at least one row is
 * selected. While disabled, hovering it explains what to do; once rows are selected the label
 * carries the count ("Reassign 3 selected claims"). The disabled label keeps the plain form
 * ("Reassign selected claims") so tests and screen-reader users have a stable name. When other state
 * blocks the action, `disabledReason` keeps the button disabled with the count in the label, and
 * hovering or tabbing to it gives the reason instead of the selection hint.
 *
 * Rule (client/AGENTS.md): hide a control when the user lacks permission; disable it with an
 * explanation when state blocks the action.
 */
export function ButtonSelection({
    selectedCount,
    action,
    noun,
    nounSingular,
    selectionHint = `Select one or more ${noun} to ${action.toLowerCase()}`,
    disabledReason,
    ...props
}: SelectionButtonProps) {
    const selectedNoun = selectedCount === 1 ? nounSingular : noun;
    const label = selectedCount ? `${action} ${selectedCount} selected ${selectedNoun}` : `${action} selected ${noun}`;

    // The label does not imply a state block, so its reason must reach keyboard users too: the base
    // Button anchors `disabledReason` to a focusable wrapper that opens the tooltip on hover or focus.
    if (isBlocked(disabledReason)) {
        return (
            <Button {...props} disabled disabledReason={disabledReason}>
                {label}
            </Button>
        );
    }

    if (selectedCount) {
        return <Button {...props}>{label}</Button>;
    }

    // A disabled native button does not receive pointer events in every browser, so the tooltip
    // anchors to a wrapping span. The button is not focusable while disabled, which is why the
    // hint is also implied by the label rather than carried only by the tooltip.
    return (
        <Tooltip title={selectionHint}>
            <span className="inline-flex">
                <Button {...props} disabled>
                    {label}
                </Button>
            </span>
        </Tooltip>
    );
}
